/**
 * Shared HTTP transport primitives for flyer-go admin REST APIs.
 */

export class HttpApiError extends Error {
  readonly status: number;
  readonly detail?: string;
  readonly body?: unknown;
  /** Stable identifier from the v4 error body — branch on this, not on `detail` (display text). */
  readonly code?: string;

  constructor(status: number, detail?: string, body?: unknown, code?: string) {
    super(detail || `HTTP API error ${status}`);
    this.name = 'HttpApiError';
    this.status = status;
    this.detail = detail;
    this.body = body;
    this.code = code;
  }
}

export function isHttpApiError(error: unknown): error is HttpApiError {
  return error instanceof HttpApiError;
}

/** Serialize an object into URL search query string, omitting undefined, null, and empty string; an array repeats the key once per element. */
export function toSearch(query?: object): string {
  if (!query) {
    return '';
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === '') continue;
        params.append(key, String(item));
      }
      continue;
    }
    params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `?${search}` : '';
}

/**
 * Extract the user-facing `detail`, error `code` and body payload from error responses (Huma JSON
 * or plain text). The v4 error body carries `message` (Vietnamese, written for the user) and
 * `detail` (English diagnostic), so the returned `detail` prefers `message`, then `detail`, then
 * `title`; the raw body stays in `body`.
 */
export function parseErrorBody(raw: string): { detail?: string; body: unknown; code?: string } {
  const text = raw.trim();
  if (!text) {
    return { body: undefined };
  }
  // a proxy or gateway page (502/503/504) is markup, never words for the user
  if (isHtml(text)) {
    return { body: text };
  }
  try {
    const parsed = JSON.parse(text) as unknown;
    if (typeof parsed === 'string') {
      return { detail: parsed, body: parsed };
    }
    if (parsed && typeof parsed === 'object') {
      const obj = parsed as {
        detail?: string;
        message?: string;
        title?: string;
        code?: string;
        errors?: { message?: string; location?: string }[];
      };
      const fieldErrors = (obj.errors ?? [])
        .map((e) => [e.location, e.message].filter(Boolean).join(': '))
        .filter(Boolean)
        .join(' · ');
      const mainMessage = obj.message || obj.detail || obj.title;
      const detail = [mainMessage, fieldErrors].filter(Boolean).join(' — ');
      return { detail: detail || undefined, body: parsed, code: obj.code };
    }
    return { body: parsed };
  } catch {
    return { detail: text, body: text };
  }
}

const isHtml = (text: string) => /^<(!doctype|html|head|body|\?xml)\b/i.test(text);

export interface FetchJsonOptions extends RequestInit {
  query?: object;
  locale?: string;
  errorFactory?: (status: number, detail?: string, body?: unknown, code?: string) => Error;
}

/**
 * Frontend slot for admin dashboards. The backend picks the session cookie
 * slot from this header (WEB_ADMIN -> `flyerId_tp` first); without it a
 * revoked `flyerId` row blocks the fallback and admin calls 401.
 * Header names are case-insensitive.
 */
export const ADMIN_DEVICE = 'WEB_ADMIN';

/**
 * Standard typed JSON fetcher for flyer-go admin endpoints.
 * Handles 204 No Content and other empty bodies, credentials inclusion, and structured error throwing.
 */
export async function fetchJson<T>(url: string, options?: FetchJsonOptions): Promise<T> {
  const search = toSearch(options?.query);
  const fullUrl = `${url}${search}`;

  const headers = new Headers(options?.headers);
  if (!headers.has('Content-Type') && options?.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  if (options?.locale && !headers.has('X-Locale')) {
    headers.set('X-Locale', options.locale);
  }
  if (!headers.has('Device')) {
    headers.set('Device', ADMIN_DEVICE);
  }
  const response = await fetch(fullUrl, {
    ...options,
    headers,
    credentials: options?.credentials ?? 'include',
  });
  if (!response.ok) {
    const raw = await response.text();
    const { detail, body, code } = parseErrorBody(raw);
    if (options?.errorFactory) {
      throw options.errorFactory(response.status, detail, body, code);
    }
    throw new HttpApiError(response.status, detail, body, code);
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  // A 202 such as the assessment regrade answers with no body.
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
